import { useMutation } from '@apollo/client';
import { DELETE_COMMUNITY_TOPIC, GET_COMMUNITY_TOPICS } from '@communities/content-management/graphql';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { useCallback, useState } from 'react';

interface DeleteCommunityTopicResponse {
  deleteCommunityTopic: boolean;
}

export function useDeleteCommunityTopic(communityId?: string) {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [deleteTopicMutation, { loading }] = useMutation<DeleteCommunityTopicResponse>(DELETE_COMMUNITY_TOPIC, {
    refetchQueries: communityId ? [{ query: GET_COMMUNITY_TOPICS, variables: { communityId } }] : [],
    awaitRefetchQueries: true,
  });

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const deleteTopic = async (communityId: string, topicName: string): Promise<boolean> => {
    try {
      track('DeleteCommunityTopicInitiated', {
        communityId,
        topicName,
      });

      await deleteTopicMutation({
        variables: {
          communityId,
          topicName,
        },
      });

      track('DeleteCommunityTopicSucceeded', {
        communityId,
        topicName,
      });

      return true;
    } catch (error) {
      track('DeleteCommunityTopicFailed', {
        communityId,
        topicName,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    deleteTopic,
    loading,
    errors,
    resetErrors,
  };
}

export default useDeleteCommunityTopic;
